import { useMemo, useRef, useState, MouseEvent, useCallback } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { User } from '@nx-frontend/shared/types';
import { NotificationPanelFilterButton } from './notification-panel-filter-button';
import { NotificationPanelFilterOption } from './notification-panel-filter-option';
import { NotificationPanelMemberIcon } from './notification-panel-member-icon';
import { $notificationPanelFilter, setNotificationPanelFilterValuesEvent } from './store';
import { getUserName } from './utils/get-user-name';

const transitionClasses: Record<string, string> = {
  entering: 'opacity-1 transform translate-x-0',
  entered: 'opacity-1 transform translate-x-0',
  exiting: 'opacity-0 transform translate-x-0',
  exited: 'opacity-0 transform translate-x-[-20px]',
};

interface NotificationPanelFilterByUserProps {
  members: User[];
}

export function NotificationPanelFilterByUser(props: NotificationPanelFilterByUserProps) {
  const t = useTranslations('notification_panel');
  const { members } = props;

  const [searchStr, setSearchStr] = useState('');

  const [notificationPanelFilter] = useUnit([$notificationPanelFilter]);
  const [setNotificationPanelFilterValues] = useUnit([setNotificationPanelFilterValuesEvent]);

  const nodeRef = useRef(null);

  const handleToggleMember = useCallback(
    (userId: number) => {
      const { userIds } = notificationPanelFilter;
      const newUserIds = userIds.find((id) => id === userId)
        ? userIds.filter((id) => id !== userId)
        : [...userIds, userId];
      setNotificationPanelFilterValues({
        userIds: newUserIds,
      });
    },
    [notificationPanelFilter, setNotificationPanelFilterValues]
  );

  const handleClear = useCallback(() => {
    setNotificationPanelFilterValues({
      userIds: [],
    });
  }, [setNotificationPanelFilterValues]);

  const items = useMemo(() => {
    return members
      .filter(({ email, firstname, lastname }) => {
        if (!searchStr) return true;
        const str = searchStr.toLocaleLowerCase();
        const matchEmail = email.toLocaleLowerCase().includes(str);
        const matchName =
          (firstname && firstname.toLocaleLowerCase().includes(str)) ||
          (lastname && lastname.toLocaleLowerCase().includes(str));

        return matchEmail || matchName;
      })
      .map((member) => {
        return (
          <NotificationPanelFilterOption
            key={member.id}
            textSize="[13px]"
            icon={<NotificationPanelMemberIcon member={member} />}
            title={getUserName(member) ?? member.email}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              handleToggleMember(member.id);
            }}
            checked={notificationPanelFilter?.userIds.includes(member.id)}
          />
        );
      });
  }, [members, searchStr, notificationPanelFilter.userIds, handleToggleMember]);

  return (
    <Transition in={notificationPanelFilter.showFilterByUser} nodeRef={nodeRef} timeout={600} unmountOnExit={true}>
      {(state: TransitionStatus) => (
        <div
          data-cy="notif-panel-filter-by-user"
          ref={nodeRef}
          data-state={state}
          className={classNames(
            'w-[256px] border border-neutral-200 rounded bg-white absolute pt-3 pb-4 shadow-[0_1.49px_11.21px_0_#A3A8AD38] left-full top-0 transition-all',
            transitionClasses[state]
          )}
        >
          <div className="px-3 mb-2">
            <input
              type="text"
              placeholder={t('notification_panel-search_user_placeholder')}
              className="w-full h-8 px-3 border border-neutral-200 rounded text-[11px] placeholder:text-neutral-500 text-neutral-800 outline-none hover:border-teal-500 focus:border-teal-500"
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => setSearchStr(e.target.value)}
              onClick={(e: MouseEvent) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto">{items}</div>
          <div className="flex pt-1.5 pr-3">
            <NotificationPanelFilterButton
              onClick={handleClear}
              title={t('notification_panel-clear')}
              additionClasses="ml-auto"
            />
          </div>
        </div>
      )}
    </Transition>
  );
}
